<!--
Booking start screen - select location, date and time
@author cm359n
-->
<template>
  <div id="contain" class="container room-booking-container">
    <Spinner ref="spinner" />

    <div id="content">
            <p>Please select a date and time to find an available room.</p>
            <Announcement category="20" />
            <br>
            <p id="noFoodDrink">You are responsible for ensuring the room you book is left as you found it. There is strictly no food or drink allowed in any study rooms.</p>
      <p class="roomMessage" v-html="roomMessage" role="alert" v-if="roomMessage"/>

  <table class="criteria" style="table-layout: fixed;">
    <!-- SELECT A ROOM -->
      <tr><td class="header-row" colspan='3'>

      <label class="no-border" for="location">Select room <span class="optional">optional</span></label><br>
      <select class="newSelect" style="margin-left:-4px;width:100%;" @change="onLocationChange" v-model="location" id="location">
        <option :value="defaultLocation[0]">{{ defaultLocation[1] }}</option>
        <option v-for="location in sortedLocations" :item="location[0]" :key="location[0]"
          :value="location[0]">
          {{ location[1] }}
        </option>
      </select>
  </td>
  </tr>
  </table>

  <!-- DATE AND TIME -->
    <TemporalCriteria
    ref="criteria"
    :v-bind:key="newDateKey"
    :advanceDays = "temporalCriteria.daysInAdvance"
    :bookingStartHour = "temporalCriteria.bookingStartHour"
    :bookingEndHour = "temporalCriteria.bookingEndHour"
    :dayTS = "day" :timeFrom = "start" :timeTo = "end"
    :disabledDayItems = []
    :constraint = "bookingConstraint"
    @clear-room-message="clearRoomMessage()"
    @change-day=" dayTS => { changeDay(dayTS) } "
    @change-start-time=" st => { start = st } "
    @change-end-time=" en => { end = en } "
    @updated-for-constraint="registerUpdated()"/>
  <!-- SELECT ATTENDEES -->
  <table class="criteria">
      <tr><td class="header-row"><label class="no-border" for="attendees">Attendees</label></td><td colspan='2'>&nbsp;</td></tr>
    <tr><td>
    <select class="newSelect" v-model="attendees" id="attendees">
      <option v-for="i in 7" :key="i">{{ i }}</option>
    </select>
  </td><td colspan='2'>&nbsp;</td></tr>
  </table>
        <div class="room-avail" v-if="location != 'ALL'">
          <button class="availabilityBtn" @click="viewAvailability(location)">{{ displayRoomAvailability ? 'Hide' : 'View' }} availability for selected date</button>
        <div v-if="displayRoomAvailability">
          <br/><RoomAvailability
          :day="getDay()"
          :location="location"
          :startingSlot="start"
          :bookingConstraint="bookingConstraint" />

        </div>
      </div>
    <div class='searchBtn'>
      <input type="button" :disabled="bookDisabled" value="Search" id="submitSearch" @click="search" class="searchButton"/>
    </div>

    <UserBookings />
  </div>

  </div>
</template>


<script setup>
//Utilities
// import $cookies from 'vue-cookies'
import * as lst from '@/components/localStorage.js'
import axios from 'axios'
import {ref, onMounted} from 'vue'
import { useRouter, useRoute } from 'vue-router'
const router = useRouter()
const route = useRoute()

//Composables
import {useUserMethods} from '@/composables/UserMethods.js'
const {isStudent} = useUserMethods()
import {useStringUtil} from '@/composables/StringMethods.js'
const {getToday, roomSort, objectForDate, timeDispUnit, hoursMinutes} = useStringUtil()

//Components
import UserBookings from '@/components/booking/UserBookings.vue'
import RoomAvailability from '@/components/booking/RoomAvailability.vue'
import TemporalCriteria from '@/components/booking/TemporalCriteria.vue'
import Spinner from '@/components/Spinner.vue'
// import Nav from '@/components/Navbar.vue'
import Announcement from '@/components/Announcement.vue'


const props = defineProps({
  testMode: String,
  givenLocation: String,
  source: String
})

//Data
// Restore last date.
let ms = Date.now()
let date = new Date()
date.setTime(ms)


let selectLocation = [ 'ALL', 'All rooms' ]

// absolute fallback hardcoded defaults
// the min/max times should come from the db
let bsh = 9
let beh = 18

const d = new Date()

const temporalCriteria = ref({
  daysInAdvance: 14,
  bookingStartHour: bsh,
  bookingEndHour: beh
})
const newDateKey = ref(d.getTime()) // a kind of $forceUpdate for TemporalCriteria
const displayRoomAvailability = ref(false)
const announcement = ref('')
const locations = ref([])
const hasRoomBookingAccess = ref(lst.getValue('hasRoomBookingAccess') === true)
const sortedLocations = ref([])
const defaultLocation = ref(selectLocation)
const location = ref(selectLocation[0])
const attendees = ref(1)
const bookingConstraint = ref({})
const maxBookingMins = ref(180)
const bookDisabled = ref(false)
const day = ref(getToday().getTime())
const start = ref('09:00')
const end = ref('10:00')
const roomMessage = ref('')
const src = ref(null)
const bookingMessage = ref('Book a Room')
const submitData = ref({})

if (isStudent()) {
  bookingMessage.value = 'Book a Study Room'
}

//Component refs
const criteria = ref(null)
const spinner = ref(null)


onMounted(
  () => {
    if (hasRoomBookingAccess.value || testMode.value === 'true') {
      console.log('---- GETLOCATIONS STARTED ----')
      getLocations()
      console.log('---- FETCHBOUNDS STARTED ----')
      fetchBounds()
      console.log('---- MOUNTED COMPLETE ----')
    } else {
      alert('You do not have permission to book rooms')
      router.push('/')
      bookDisabled.value = true
    }
  }
)

function registerUpdated () {
  console.log('** registerUpdated called **')
  // this is here to change the v-bind:key for TemporalCriteria when a room is selected, updating its constraint.
  // Sat/Sun may need to be disabled, so the changing of the key will/should force a re-render.
  const d = new Date()
  newDateKey.value = d.getTime()
}

function clearRoomMessage () {
  roomMessage.value = ''
  bookDisabled.value = false
}

function getDay () {
  const d = new Date()
  const MS_IN_DAY = 86400000
  const diffDays = (day.value - d.getTime()) / MS_IN_DAY
  return Math.ceil(diffDays)
}

function viewAvailability () {
  displayRoomAvailability.value = !displayRoomAvailability.value
}

function getHour (timeStr) {
  let ret = null
  if (typeof timeStr !== 'undefined' && timeStr != null) {
    if (timeStr.indexOf(':') != -1) {
      let hrmin = timeStr.split(':')
      ret = parseInt(hrmin[0])
      let min = parseInt(hrmin[1])
      if (min != 0) {
        ret += 1
      }
    }
  }
  return ret
}

function fetchBounds () {
  axios.get('/timetable/bookingv2/bounds', {})
    .then(response => {
      if (typeof response.data !== 'undefined') {
        let bounds = response.data[0]
        // bounds: [0 - min hour, 1 - max hour, 2 - max booking duration, 3 - max days in advance]
        console.log('Allowed start time seems to be ' + bounds[0] + ' and end time ' + bounds[1])
        temporalCriteria.value.bookingStartHour = getHour(bounds[0])
        temporalCriteria.value.bookingEndHour = getHour(bounds[1])
        let mbm = bounds[2]
        if (typeof mbm !== 'undefined' && mbm != null) {
          console.log('max booking duration of ' + mbm)
          maxBookingMins.value = parseInt(mbm)
        }
        let mdy = bounds[3]
        if (typeof mdy !== 'undefined' && mdy != null) {
          console.log('can book ' + mdy + ' day(s) in advance')
          let maxdate = bounds[4]
          if (mdy == 999 && typeof maxdate !== 'undefined' && maxdate != null) {
            const dte = new Date(maxdate)
            console.log('absolute maximum is really at ' + dte)
            let today = getToday()
            const MS_IN_DAY = 86400000
            const numDays =  (maxdate - today.getTime()) / MS_IN_DAY
            console.log(' - we would need to shorten the dropdown to ' + numDays + ' days')
            mdy = numDays
          }
          temporalCriteria.value.daysInAdvance = parseInt(mdy)
          criteria.value.updateMe(temporalCriteria.value)
        }
      }
    })
}

function hrMin (d) {
  let dhr = d.getHours() + ''
  let dmin = d.getMinutes() - 30 + ''
  let ddisp = dhr.padStart(2, '0') + ':' + dmin.padStart(2, '0')
  return ddisp
}

function checkForQR () {
  console.log('.... checking for QR code')
  // this.src = this.qr
  const src = route.query.src
  if (props.givenLocation != null) {
    let roomFound = false
    let roomId = props.givenLocation
    console.log('src = ' + src + ', roomId = ' + roomId)
    locations.value.forEach(singleLocation => {
      if (singleLocation[0] === roomId) {
        console.log('qr location found!')
        roomFound = true
        location.value = singleLocation[0]
      }
    })
    if (!roomFound) {
      alert('The room for this QR/NFC code was not found.')
      // well, we're on findaspace so don't go all the way back to the homepage
      router.push('/findaspace/0')
    }
  }
}

function getBookingConstraint () {
  if (location.value !== 'ALL') {
    axios.get('/timetable/booking/constraint', {
      params: {
        locationId: location.value
      }
    }).then(response => {
      bookingConstraint.value = response.data
      if (typeof bookingConstraint.value.maxDuration !== 'undefined') {
        console.log('max duration for this location is ' + bookingConstraint.value.maxDuration)
      }
    })
      .finally(() => {
        // this.checkDateConstraints()
      })
  } else {
    // search all the rooms, no booking constraints
    bookingConstraint.value = {}
    roomMessage.value = ''
    bookDisabled.value = false
  }
}

function createData () {
  let dte = new Date()
  dte.setTime(day.value)
  let dteObj = objectForDate(dte)

  submitData.value.location = location.value
  submitData.value.bookingDate = dteObj['oracleDate']
  submitData.value.startTime = start.value
  submitData.value.endTime = end.value
  submitData.value.attendees = attendees.value
  console.log(typeof attendees.value)
}

function dataMissing (keys) {
  let ret = false
  keys.forEach((k) => {
    let x =  submitData.value[k]
    if (typeof x === 'undefined' || x == '') {
      console.log('WARNING key ' + k + ' is missing a value!')
      ret = true
    }
  })
  return ret
}

function submitForm () {
  // we won't do any checks til submission time. too many levers to play with in this screen.
  let errors = checkTimeConstraints()

  // if the weekend is hidden, the first option is not today so be careful
  let dte = new Date()
  let d = objectForDate(dte)
  let currentTime = hrMin(dte)

  // check data array
  if (dataMissing(['location', 'bookingDate', 'startTime', 'endTime', 'attendees'])) {
    errors.push('Some of the appointment data could not be collected. Please contact support.')
  }

  let selectedDate = objectForDate(new Date(day.value)).display

  if (d.display == selectedDate) {
    console.log('booking for today')
    if (currentTime > submitData.value.startTime) {
      errors.push('Appointment cannot be in the past')
    }
  }

  if (errors.length > 0) {
    roomMessage.value = 'Please correct the following:<ul>' +
      errors.map((x) => '<li>' + x + '</li>').join('') +
      '</ul>'
  } else {
    router.push({
      name: 'bookingResults',
      params: submitData.value
    })
  }
}

// Fetches the locations list for the user
function getLocations () {
  spinner.value.spin()
  axios.get('/timetable/booking/locations')
    .then(response => {
      locations.value = response.data
      alphabet()
    })
    .finally(() => {
      spinner.value.stop()
      checkForQR()
    })
}

function alphabet () {
  sortedLocations.value = locations.value
  sortedLocations.value.sort(roomSort) // see StringUtil.js
}

function checkTimeConstraints () {
  roomMessage.value = ''

  let minTime = timeDispUnit(temporalCriteria.value.bookingStartHour) + ':00'
  let maxTime = timeDispUnit(temporalCriteria.value.bookingEndHour) + ':00'
  let maxDuration = 180

  if (typeof maxBookingMins.value !== 'undefined') {
    // make it easy and override existing time restrictions if there were any
    // minTime = '09:00'
    // maxTime = '18:00'
    maxDuration = maxBookingMins.value
  } else {
    if (typeof bookingConstraint.value.minTime !== 'undefined') {
      minTime = bookingConstraint.value.minTime
    }
    if (typeof bookingConstraint.value.maxTime !== 'undefined') {
      maxTime = bookingConstraint.value.maxTime
    }
    if (typeof bookingConstraint.value.maxDuration !== 'undefined') {
      maxDuration = bookingConstraint.value.maxDuration
    }
  }

  let errorMsg = []

  // easy stuff
  if (start.value >= end.value) {
    errorMsg.push('End time should be after start time')
  }

  // min and max times
  if (typeof minTime !== 'undefined' && minTime > start.value) {
    errorMsg.push('You cannot book the room before ' + minTime)
    // this.start = minTime
  }
  if (typeof maxTime !== 'undefined' && maxTime < end.value) {
    errorMsg.push('You cannot book the room after ' + maxTime)
    // this.end = maxTime
  }
  console.log('currently maxDuration is ' + maxDuration)
  if (typeof maxDuration !== 'undefined') {
    let st = start.value
    let et = end.value
    let duration = hoursMinutes(et) - hoursMinutes(st)
    if (duration > maxDuration) {
      errorMsg.push('The room can only be booked for a maximum of ' + maxDuration + ' minutes.')
      // this.end = this.start
      console.log(start.value + ' - ' + end.value)
    }
  }
  return errorMsg
}

function onLocationChange () {
  // this.toggleBookButton()
  getBookingConstraint()
  roomMessage.value = ''
  displayRoomAvailability.value = false
}

function changeDay (dayTS) {
  day.value = dayTS
  displayRoomAvailability.value = false
}

function search () {
  // $('.searchButton').button('disable')
  bookDisabled.value = true
  createData()
  submitForm()
}

</script>


<style scoped>
.newSelect {
  border:0px;
  font-family:PTSans;
  color:#4F5961;
  font-size:16px;
  margin-left:-2px;
  width:100%;
}
table.criteria {
  width: 100%;
}
label.no-border {
  font-weight: 700;
}
select.custom-select.select-sd {
  box-sizing: border-box;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
}

select.custom-select > option:disabled {
  background: #eee;
  font-style: italic;
}

div#content {
  margin-top: 23px;
  margin-left: 24px;
  margin-right: 24px;
}

p {
  font-family: PTSans;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
 /* line-height: 21px;*/
}

select#attendees {
   width: 80px;
}

.optional {
    font-style: normal;
    font-weight: normal;
    font-size: 11px;
    line-height: 14px;
}

td.header-row {
  padding-top: 10px;
}

div.booking {
    margin-top:30px;
    margin-bottom:100px;
}

label.no-border {
  margin-bottom: 0px;
  color: var(--uog-dark-blue);
}
div.searchBtn {
  margin-top: 20px;
  margin-bottom: 30px;
}

.heading {
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 21px;
  color: var(--uog-dark-blue);
}

.mandatory {
  color: red;
}

select.round-border {
    border: 1px solid var(--uog-dark-blue);
      border-radius: 8px;
}

td.no-border-td {
  padding: 0px 20px 0px 0px;
}

select.no-border {
  border: none;
  padding: 1px;
  height: 28px;
  background: url('~@/assets/br_down.png') no-repeat right #fff;
  background-position-x: right;
}

p.roomMessage {
  color: red;
}
input.searchButton:disabled {
  background: #9512726b;
}

.room-avail {
  margin-top: 10px;
}
input.searchButton {
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 21px;
  align-items: center;
  text-align: center;
  color: #FFFFFF;
  height: 55px;
  width: 100%;

  background: var(--uog-dark-blue);
  border-radius: 12px;
  border-color: var(--uog-dark-blue);
  border-style: solid;
}
button.availabilityBtn {
  border: 0;
  background: white;
  text-decoration: underline;
}

#noFoodDrink {
  font-weight: bold;
}

</style>
